import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { AppTexture } from './app/AppTexture'
import { ErrorBoundary } from './platform/async/ErrorBoundary'
import { AppCrashed } from './routes/errors/AppCrashed'
import { MaintenanceGate } from './app/MaintenanceGate'
import { QueryProvider } from './app/QueryProvider'
import { ThemeProvider } from './app/ThemeProvider'
import { AuthProvider } from './auth/AuthProvider'
import { OfflineBanner } from './platform/feedback/OfflineBanner'
import { ToastProvider } from './platform/feedback/ToastProvider'
import { BillingProvider } from './billing/BillingProvider'
import { billingPortFromEnv } from './billing/billingPort'
import { entitlementsForTier } from './platform/access/entitlementsForTier'
import { BUILD_TIER, TIER } from './platform/access/tier'
import { createFirebaseAuthPort, currentFirebaseApp, initAnalytics } from './auth/firebaseAdapter'
import { env } from './config/env'
import { startSession } from './auth/session'
import AppRoutes from './App'
import { startTelemetry } from './platform/analytics/telemetry'
import { reportRenderError } from './platform/analytics/errorReporter'
/**
 * The typeface, self-hosted — #91.
 *
 * `reset.css` has named `'Instrument Sans'` since Phase 1 and nothing ever
 * loaded it, so every screen rendered in the `system-ui` fallback. The design
 * source loads exactly these four weights from Google Fonts and uses all four;
 * 600 is its most common by a wide margin.
 *
 * Self-hosted rather than a `<link>` to `fonts.googleapis.com`, because #51,
 * #79 and #86 are adding a Content-Security-Policy and an external font host
 * would be a `font-src`/`style-src` exemption granted on day one. It also
 * removes a third-party request from every page load.
 *
 * The STATIC package, not `@fontsource-variable/*`: the variable build
 * registers as `'Instrument Sans Variable'`, which does not match the family
 * reset.css declares — importing it would reproduce #91 exactly, with a
 * dependency in package.json to make it look fixed. `typography.test.ts`
 * asserts the join rather than either half.
 */
import '@fontsource/instrument-sans/400.css'
import '@fontsource/instrument-sans/500.css'
import '@fontsource/instrument-sans/600.css'
import '@fontsource/instrument-sans/700.css'
/**
 * Roboto Medium, for one control: the Google sign-in button.
 *
 * Google's identity branding guidelines specify Roboto Medium for the button
 * label, and that is not a stylistic preference we get to override — the same
 * document forbids recolouring or redrawing the mark. Weight 500 only; nothing
 * else in this app uses Roboto and importing the family would ship four faces
 * to style one button.
 *
 * `@fontsource`, like Instrument Sans, so it is self-hosted. A Google Fonts
 * <link> would be an external request, which the CSP work (#51, #79, #86) is
 * heading towards forbidding, and would make sign-in depend on a third-party
 * origin being reachable.
 */
import '@fontsource/roboto/500.css'
import './styles/tokens.css'
import './styles/reset.css'
import './styles/motion.css'

// The scaffold ships `getElementById('root')!`, and so does Task 18's step 5.
// A non-null assertion to quiet the compiler is exactly what strict mode is for
// — if the element is ever missing, a named error beats "Cannot read properties
// of null". CLAUDE.md's bar says the same. Kept deliberately against the plan.
const root = document.getElementById('root')
if (!root) throw new Error('Root element #root is missing from index.html')

/**
 * Module scope, not inside the render, for two reasons that both bite.
 *
 * A port rebuilt per commit would re-subscribe the provider on every render;
 * and `initializeApp` is not idempotent across distinct config objects, which
 * is why `firebaseAdapter` also refuses to reuse an app built for a different
 * project.
 *
 * **This is the only module that reads `env.firebase`**, and `firebaseAdapter`
 * is the only one that imports `firebase/*` — a test asserts the second. The
 * read is eager, at module scope, deliberately: `env`'s credential fields are
 * getters (a Phase 7 credential used to blank this file at import), and a
 * getter defers the failure to first access. Reading it here keeps a missing
 * C2 a startup failure rather than a caught error inside an error boundary.
 *
 * Replaces `createUnavailablePort()`, which is deleted in this commit — its own
 * header says nothing else may import it, so the delete is the whole migration.
 */
// The measurement id rides in the app's options because `initializeAnalytics`
// reads it there; absent (OPTIONAL, C4) means analytics stays off (#112).
const port = createFirebaseAuthPort({ ...env.firebase, measurementId: env.measurementId })
const firebaseApp = currentFirebaseApp()
startTelemetry({
  sentryDsn: env.sentryDsn,
  mode: import.meta.env.MODE,
  startAnalytics: env.measurementId && firebaseApp ? () => initAnalytics(firebaseApp) : undefined,
})

/** Null with a dummy, missing or malformed key — gates then stay locked. The SDK
 *  itself is lazy-loaded on first sign-in, so building this costs nothing. #102. */
const billingPort = billingPortFromEnv()

/**
 * Render crashes reach Sentry from HERE. The app is wrapped in an
 * `ErrorBoundary`, and a caught error is never rethrown, so Sentry's global
 * handlers see none of them. React 19's root callbacks are the only place both
 * kinds arrive. Supplying either REPLACES React's default logging, so each
 * keeps a `console.error` — the boundary logs its own caught errors, so only the
 * uncaught path needs the explicit one.
 */
createRoot(root, {
  onCaughtError: (error) => {
    reportRenderError(error)
  },
  onUncaughtError: (error) => {
    console.error(error)
    reportRenderError(error)
  },
}).render(
  <StrictMode>
    {/* ThemeProvider outermost: it writes `data-theme` on body, which every
        token below it resolves against. QueryProvider next, so any surface can
        fetch. AppTexture paints under the router rather than inside a route,
        because it belongs to the page and not to any one screen. */}
    <ThemeProvider>
      <QueryProvider>
        {/* Decorative, generative, and above the router — so outside every
            layout boundary. `fallback={null}` because a background layer that
            fails should disappear, not put an error panel at the top of the
            app. #95. */}
        <ErrorBoundary name="Background texture" fallback={null}>
          <AppTexture />
        </ErrorBoundary>
        {/* #274. Above the router for the same reason `AppTexture` is: it has
            to survive every navigation, and `useOnline()`'s window listeners
            do not care which route is mounted. `fallback={null}` for the same
            reason as the texture above — a strip that cannot render its own
            connectivity status must not put an error panel at the top of every
            page; the app underneath is what this component exists to keep
            visible. */}
        <ErrorBoundary name="Offline banner" fallback={null}>
          <OfflineBanner />
        </ErrorBoundary>
        {/**
         * The last boundary — everything the app is, with a whole-screen
         * fallback instead of a panel.
         *
         * `ErrorBoundary`'s default fallback is an `Async` error card sized for
         * ONE failed surface inside a working page. That is right where a chart
         * or a grid dies and the shell survives. It is wrong here: if the
         * router, a provider or a layout throws, there is no page left to put a
         * card into, and the default would render a small panel floating on a
         * blank document.
         *
         * **Outside `BrowserRouter`, deliberately.** A crash in the router
         * itself has to be catchable, and this boundary cannot sit inside the
         * thing most likely to have failed. `AppCrashed` is built for that: it
         * navigates with plain `<a href>` rather than `Link`/`useNavigate`,
         * which throw with no router above them.
         *
         * `name` is what the screen shows under "What failed", so it is written
         * for a person rather than as a component id.
         */}
        <ErrorBoundary name="TrackE5" fallback={<AppCrashed name="TrackE5" />}>
        <BrowserRouter>
          {/* #274. Inside the router — `Maintenance.tsx` renders `<Link to="/">`
              for its brand mark, which throws with no router above it, unlike
              `AppCrashed`'s deliberate `<a href>`. Above everything else inside
              it: a maintenance window replaces auth, toasts and every route
              alike, so it sits outside `AuthProvider` rather than being one
              more thing a signed-in tree has to account for. See
              MaintenanceGate.tsx for the fuller reasoning. */}
          <MaintenanceGate>
            {/* Inside the router, so anything auth-related may navigate. Outside
                the routes, so one session serves the whole table rather than
                being torn down and rebuilt on every navigation. */}
            <AuthProvider port={port} startSession={startSession}>
              {/* Inside AuthProvider so an auth screen can report an outcome, and
                  outside the routes so a toast raised by a mutation survives the
                  navigation that mutation causes. It portals its own host to
                  <body>, so its position in this tree decides who may CALL it,
                  not where it paints. */}
              <ToastProvider>
                {/* #102 — nothing populated this, so every gate read locked and
                    `custom_categories` made category creation unreachable for
                    everyone including the developer. `BillingProvider` now owns
                    the provider: `TIER` is its `baseline`, and a signed-in
                    user's RevenueCat entitlements are added to it.

                    #477 — `TIER` (`platform/access/tier.ts`) replaces #460's
                    boolean `MODE !== 'production'` gate. That boolean granted
                    every entitlement on EVERY non-production build alike,
                    which erased the client's only enforcement of the
                    one-manual-account cap on staging — the tenant holding
                    real bank data (Plaid PRODUCTION credentials over the dev
                    database — `config/backendOverride.ts`'s `BACKENDS`
                    docblock). No production environment is deployed
                    anywhere today, dev and staging are the only two live
                    ones, so `TIER` narrowing staging to `'free'` is not an
                    analogy to production — it is the one live build an
                    unenforced cap would act on. Dev (the vite dev server and
                    `build:dev`) stays `'premium'` so #460's own goal — every
                    gated surface reachable without a RevenueCat session —
                    still holds there. It also folds in a developer override,
                    honoured everywhere except production; see that module's
                    docblock. `main.test.ts` pins this call site directly, MODE
                    by MODE, override included. */}
                {/* `baseline` is what the BUILD grants (the tier above);
                    RevenueCat adds the signed-in user's own, never household.
                    `main.test.ts` pins `baseline` and `port`, MODE by MODE.

                    A developer override away from the build's own tier means
                    "act as this tier", so billing is switched off then: grants
                    are baseline + billing, and a real subscriber forcing Free
                    would otherwise still hold what they pay for. */}
                <BillingProvider
                  port={TIER === BUILD_TIER ? billingPort : null}
                  baseline={entitlementsForTier(TIER)}
                >
                  <AppRoutes />
                </BillingProvider>
              </ToastProvider>
            </AuthProvider>
          </MaintenanceGate>
        </BrowserRouter>
        </ErrorBoundary>
      </QueryProvider>
    </ThemeProvider>
  </StrictMode>,
)
